import React from 'react'
import moment from 'moment'
import _cx from 'classnames/bind'
import PropTypes from 'prop-types'
import { Text, Table, Grid } from '@adc/ui-adc'
import { formatMoneyWithNumeral } from '../../utils/formatters'
import { buildRow, DataNotAvailable, privateView } from './utils'
import cls from './index.scss'

const cx = _cx.bind(cls)

const elmId = 'sale_tax_history'

const transactionTypes = {
  list: 'Listed',
  sold: 'Sold',
  withdrn: 'Withdrawn',
  exp: 'Expired',
  pnd: 'Pending',
}

const FIELDS = [
  {
    label: 'Transaction',
    fieldPath: 'type',
    format: type => transactionTypes[type],
  },
  {
    label: 'Price',
    fieldPath: 'amount',
    format: amount => formatMoneyWithNumeral(amount, '-'),
  },
  {
    label: 'Date',
    fieldPath: 'date',
    format: date => moment(date).format('MM/DD/YYYY'),
  },
  {
    label: 'Source',
    fieldPath: 'datasource',
    format: source =>
      ['dataquick', 'Public Records'].includes(source)
        ? 'Public Records'
        : 'MLS',
  },
]

const TaxColumn = ({ label, value, otherValues }) => (
  <Grid.Column>
    <Text color='gray'>{label}</Text>
    <Text className='u-mt-1 u-mb-1' weight='heavy' size='massive'>
      {formatMoneyWithNumeral(value, 'N/A')}
    </Text>
    {otherValues.map(({ label, value }) => (
      <div key={label} className={cx('othervalues')}>
        <Text color='gray'>{label}</Text>
        <Text color='gray' weight='heavy'>
          {value || 'N/A'}
        </Text>
      </div>
    ))}
  </Grid.Column>
)

TaxColumn.propTypes = {
  label: PropTypes.string,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  otherValues: PropTypes.array,
}

const SaleTaxHistory = ({ saleHistory, taxInfo, loading }) => {
  if (loading) return null

  const hasHistory = saleHistory.length > 0

  return !hasHistory ? (
    <DataNotAvailable />
  ) : (
    <>
      <Text size='large' weight='heavy' className={cx('u-mb-4')}>
        Property Sale History
      </Text>
      <div className={cx('table')}>
        <Table unstackable>
          <Table.Header>
            <Table.Row>
              {FIELDS.map(field => (
                <Table.HeaderCell key={field.label}>
                  {field.label}
                </Table.HeaderCell>
              ))}
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {saleHistory.map((comp, i) =>
              buildRow({
                comp,
                elmId,
                fields: FIELDS,
                key: i,
              })
            )}
          </Table.Body>
        </Table>
      </div>
      <Text size='large' weight='heavy' className={cx('u-mt-6')}>
        County Tax & Assessment
      </Text>
      <Grid stackable columns={3} centered padded>
        <Grid.Row centered>
          <TaxColumn
            label='Assessed Land Value'
            value={taxInfo.landvalue}
            otherValues={[
              {
                label: 'Assessment Year',
                value: taxInfo.assessedyear,
              },
              {
                label: 'Tax Year',
                value: taxInfo.taxyear,
              },
            ]}
          />
          <TaxColumn
            label='Assessed Improvement'
            value={taxInfo.improvementvalue}
            otherValues={[
              {
                label: 'Total Tax Amount',
                value: formatMoneyWithNumeral(taxInfo.taxvalue, 'N/A'),
              },
              {
                label: 'Delinquent Year',
                value: taxInfo.deliquentyear,
              },
            ]}
          />
          <TaxColumn
            label='Total Assessed Value'
            value={taxInfo.assessedvalue}
            otherValues={[
              {
                label: 'Homeowners Exemption',
                value: formatMoneyWithNumeral(taxInfo.taxexemption, 'N/A'),
              },
            ]}
          />
        </Grid.Row>
      </Grid>
    </>
  )
}

SaleTaxHistory.propTypes = {
  saleHistory: PropTypes.array.isRequired,
  taxInfo: PropTypes.object,
  loading: PropTypes.bool,
}

export default privateView(SaleTaxHistory)
